import { useCallback, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './DropZone.module.css';

export function DropZone({
  accept,
  multiple,
  onFiles,
  children,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  children?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);

  const handle = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      onFiles(multiple ? Array.from(files) : [files[0]!]);
    },
    [multiple, onFiles]
  );

  return (
    <div
      className={clsx(styles.zone, active && styles.active)}
      onDragOver={(e: DragEvent) => {
        e.preventDefault();
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e: DragEvent) => {
        e.preventDefault();
        setActive(false);
        handle(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
    >
      <input
        ref={inputRef}
        type="file"
        className={styles.hidden}
        accept={accept}
        multiple={multiple}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          handle(e.target.files);
          e.target.value = '';
        }}
      />
      {children ?? (
        <>
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Drop files here</h3>
          <p>or click to browse</p>
        </>
      )}
    </div>
  );
}
